import {Component, OnDestroy, OnInit} from '@angular/core';
import {Observable, Subscription} from "rxjs";
import {Broadcast} from "../model/broadcast/broadcast";
import {AuthService} from "../services/auth.service";
import {BroadcastsService} from "../services/broadcasts.service";
import {BroadcastDialogComponent} from "../broadcast-dialog/broadcast-dialog.component";
import {map} from "rxjs/operators";
import {GamesService} from "../services/games.service";
import {Game} from "../model/broadcast/game";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {MatTableModule} from "@angular/material/table";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {RouterLink} from "@angular/router";
import {NgIf, AsyncPipe, DatePipe} from "@angular/common";
import {User} from "../model/user";
import {UserPickerComponent} from "../shared/user-picker/user-picker.component";
import {ChangeOwnerDialogComponent} from "./change-owner-dialog/change-owner-dialog.component";

@Component({
    selector: 'app-broadcast-list',
    templateUrl: './broadcast-list.component.html',
    styleUrls: ['./broadcast-list.component.scss'],
    standalone: true,
    imports: [
        MatDialogModule,
        MatTableModule,
        MatButtonModule,
        MatIconModule,
        RouterLink,
        NgIf,
        AsyncPipe,
        DatePipe,
        UserPickerComponent
    ]
})
export class BroadcastListComponent implements OnInit, OnDestroy {

  broadcasts$: Observable<Broadcast[]>;
  games: Game[] = [];
  subscription: Subscription;
  linkedBroadcastUid: string | null = null;
  selectedOwnerUid: string;
  selectedOwnerLabel: string | null = null;
  private linkedSub: Subscription;
  private gameSubs = new Map<string, Subscription>();
  private broadcastsCount = 0;

  constructor(
    public authService: AuthService,
    public broadcastService: BroadcastsService,
    private gamesService: GamesService,
    private dialog: MatDialog,
  ) { }

  get isAdmin(): boolean {
    return this.authService.user?.role === 'admin';
  }

  get viewingSelf(): boolean {
    return this.selectedOwnerUid === this.authService.user?.uid;
  }

  ngOnInit(): void {
    this.linkedSub = this.broadcastService.getLinkedBroadcastUid().subscribe(uid => {
      this.linkedBroadcastUid = uid ?? null;
    });
    this.authService.user$.subscribe(user => {
      if (!user) return;
      this.selectedOwnerUid = user.uid;
      this.selectedOwnerLabel = null;
      this.loadBroadcasts(user.uid);
    });
  }

  onOwnerSelected(user: User): void {
    this.selectedOwnerUid = user.uid;
    this.selectedOwnerLabel = user.displayName || user.email;
    this.loadBroadcasts(user.uid);
  }

  backToMyBroadcasts(): void {
    this.selectedOwnerUid = this.authService.user.uid;
    this.selectedOwnerLabel = null;
    this.loadBroadcasts(this.selectedOwnerUid);
  }

  openChangeOwner(broadcast: Broadcast): void {
    const dialogRef = this.dialog.open(ChangeOwnerDialogComponent, {width: '480px'});
    dialogRef.afterClosed().subscribe((newOwner: User | undefined) => {
      if (newOwner && newOwner.uid !== broadcast.ownerUid) {
        this.broadcastService.changeOwner(broadcast.uid, newOwner.uid);
      }
    });
  }

  private loadBroadcasts(ownerUid: string): void {
    this.subscription?.unsubscribe();
    this.broadcasts$ = this.broadcastService.getBroadcastsByOwner(ownerUid).pipe(
      map(list => [...list].sort((a, b) => {
        const ta = a.scheduled?.toMillis() ?? this.games[a.gameId]?.scheduled?.toMillis() ?? 0;
        const tb = b.scheduled?.toMillis() ?? this.games[b.gameId]?.scheduled?.toMillis() ?? 0;
        return ta - tb;
      })),
    );
    this.subscription = this.broadcasts$.subscribe(myBroadcasts => {
      this.broadcastsCount = myBroadcasts.length;
      const seen = new Set<string>();
      myBroadcasts.forEach(b => {
        if (!b.gameId) return;
        seen.add(b.gameId);
        if (!this.gameSubs.has(b.gameId)) {
          const sub = this.gamesService.getGame(b.gameId).subscribe(g => {
            this.games[b.gameId] = g;
          });
          this.gameSubs.set(b.gameId, sub);
        }
      });
      for (const [gid, sub] of this.gameSubs) {
        if (!seen.has(gid)) {
          sub.unsubscribe();
          this.gameSubs.delete(gid);
          delete this.games[gid];
        }
      }
    });
  }

  copyToClipboard(broadcast: Broadcast) {

  }

  createBroadcast(): void {
    const wasEmpty = this.broadcastsCount === 0;
    const dialogRef = this.dialog.open(BroadcastDialogComponent, {
      width: '800px',
    });
    dialogRef.afterClosed().subscribe((newUid: string | undefined) => {
      if (wasEmpty && newUid) {
        this.broadcastService.linkBroadcastToUserOutput(newUid);
      }
    });
  }

  deleteBroadcast(broadcast: Broadcast) {
    this.broadcastService.deleteBroadcast(broadcast);
  }

  linkBroadcast(broadcast: Broadcast) {
    this.broadcastService.linkBroadcastToUserOutput(broadcast.uid);
  }

  isBroadcastLinked(broadcast: Broadcast): Observable<boolean> {
    return this.broadcastService.getLinkedBroadcastUid().pipe(map(linkedBroadcastUid => {
      return linkedBroadcastUid == broadcast.uid;
    }));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.linkedSub?.unsubscribe();
    this.gameSubs.forEach(sub => sub.unsubscribe());
    this.gameSubs.clear();
  }
}
